import React, { useState } from 'react';
import { School } from '../../../types/admin';

interface CreateSchoolFormProps {
  onCancel: () => void;
  onCreate: (school: Partial<School>) => Promise<void>;
  loading: boolean;
}

export default function CreateSchoolForm({ onCancel, onCreate, loading }: CreateSchoolFormProps) {
  const [newSchool, setNewSchool] = useState({
    name: '',
    type: 'public' as const,
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    website: '',
    gradeRange: {
      start: 'K',
      end: '12'
    }
  });

  const handleSubmit = async () => {
    if (!newSchool.name.trim()) return;
    await onCreate(newSchool);
  };

  const gradeOptions = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] max-h-[800px]">
      <div className="p-6 border-b border-border-light">
        <h2 className="text-lg font-semibold text-text-primary">Create New School</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-sm font-medium text-text-secondary mb-3">Basic Information</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  School Name
                </label>
                <input
                  type="text"
                  value={newSchool.name}
                  onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  placeholder="e.g., Springfield High School"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  School Type
                </label>
                <select
                  value={newSchool.type}
                  onChange={(e) => setNewSchool({ ...newSchool, type: e.target.value as School['type'] })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                >
                  <option value="public">Public School</option>
                  <option value="private">Private School</option>
                  <option value="charter">Charter School</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Grade Range - Start
                  </label>
                  <select
                    value={newSchool.gradeRange.start}
                    onChange={(e) => setNewSchool({
                      ...newSchool,
                      gradeRange: { ...newSchool.gradeRange, start: e.target.value }
                    })}
                    className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  >
                    {gradeOptions.map(grade => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Grade Range - End
                  </label>
                  <select
                    value={newSchool.gradeRange.end}
                    onChange={(e) => setNewSchool({
                      ...newSchool,
                      gradeRange: { ...newSchool.gradeRange, end: e.target.value }
                    })}
                    className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  >
                    {gradeOptions.map(grade => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-sm font-medium text-text-secondary mb-3">Contact Information</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={newSchool.address}
                  onChange={(e) => setNewSchool({ ...newSchool, address: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  placeholder="Street address"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={newSchool.city}
                    onChange={(e) => setNewSchool({ ...newSchool, city: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={newSchool.state}
                    onChange={(e) => setNewSchool({ ...newSchool, state: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    placeholder="State"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={newSchool.zipCode}
                    onChange={(e) => setNewSchool({ ...newSchool, zipCode: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    placeholder="ZIP code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={newSchool.phone}
                    onChange={(e) => setNewSchool({ ...newSchool, phone: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    placeholder="Phone number"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={newSchool.website}
                  onChange={(e) => setNewSchool({ ...newSchool, website: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  placeholder="https://www.school.edu"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 border-t border-border-light bg-surface-light">
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !newSchool.name.trim()}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                     transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create School'}
          </button>
        </div>
      </div>
    </div>
  );
}